<section class="preview marketing-email-shell" [hidden]="!visible">
  <div class="marketing-email-locked" *ngIf="!authToken">
    <h2>Email Marketing</h2>
    <p>Sign in with Google to manage contacts, layouts, campaigns, and compliant sends.</p>
  </div>

  <div class="marketing-email-workspace" *ngIf="authToken">
    <section class="marketing-email-warning" role="alert" aria-live="polite">
      <strong>Experimental Feature</strong>
      <p>Email marketing is experimental. Review contacts, sender details, compliance content, and campaign copy before sending.</p>
    </section>

    <ion-grid>
      <ion-row>
        <ion-col size="12" size-xl="4">
          <form class="marketing-email-panel" [formGroup]="contactForm" (ngSubmit)="createContact()">
            <div class="history-header">
              <h2>Contacts</h2>
              <ion-button type="submit" size="small" fill="outline" [disabled]="isSavingContact">Add</ion-button>
            </div>
            <ion-list lines="full">
              <ion-item>
                <ion-label position="stacked">Email</ion-label>
                <ion-input type="email" formControlName="email" placeholder="customer@example.com"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Name</ion-label>
                <ion-input formControlName="displayName" placeholder="Jane Smith"></ion-input>
              </ion-item>
            </ion-list>
            <ion-note color="medium" *ngIf="contactMessage">{{ contactMessage }}</ion-note>
          </form>

          <form class="marketing-email-panel" [formGroup]="contactImportForm" (ngSubmit)="importContacts()">
            <div class="history-header">
              <h2>Import Contacts</h2>
              <ion-button type="submit" size="small" fill="outline" [disabled]="isImportingContacts">
                <ion-spinner *ngIf="isImportingContacts" name="crescent"></ion-spinner>
                <span *ngIf="!isImportingContacts">Import</span>
              </ion-button>
            </div>
            <ion-list lines="full">
              <ion-item>
                <ion-label position="stacked">CSV or JSON</ion-label>
                <ion-textarea
                  formControlName="importText"
                  autoGrow="true"
                  rows="6"
                  placeholder='email,display_name&#10;jane@example.com,Jane Smith&#10;&#10;[{"email":"sam@example.com","display_name":"Sam Lee"}]'
                ></ion-textarea>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Upload</ion-label>
                <input class="file-input" type="file" accept=".csv,.json,text/csv,application/json" aria-label="Import marketing contacts" (change)="onContactImportFileSelected($event)">
              </ion-item>
            </ion-list>
            <ion-note color="medium" *ngIf="contactImportMessage">{{ contactImportMessage }}</ion-note>
          </form>

          <form class="marketing-email-panel" [formGroup]="listForm" (ngSubmit)="saveContactList()">
            <div class="history-header">
              <h2>Lists</h2>
              <ion-button type="submit" size="small" fill="outline" [disabled]="isSavingList">Save</ion-button>
            </div>
            <ion-list lines="full">
              <ion-item>
                <ion-label position="stacked">List Name</ion-label>
                <ion-input formControlName="name" placeholder="Spring launch leads"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Description</ion-label>
                <ion-textarea formControlName="description" autoGrow="true" rows="2"></ion-textarea>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Contacts</ion-label>
                <ion-select formControlName="contactIds" multiple="true" interface="popover" placeholder="Select recipients">
                  <ion-select-option *ngFor="let contact of contacts" [value]="contact.id">
                    {{ contact.display_name || contact.email }} - {{ contact.email }}
                  </ion-select-option>
                </ion-select>
              </ion-item>
            </ion-list>
            <ion-note color="medium" *ngIf="listMessage">{{ listMessage }}</ion-note>
          </form>

          <form class="marketing-email-panel" [formGroup]="senderForm" (ngSubmit)="saveSenderIdentity()">
            <div class="history-header">
              <h2>Sender</h2>
              <ion-button type="submit" size="small" fill="outline" [disabled]="isSavingSender">Save</ion-button>
            </div>
            <ion-list lines="full">
              <ion-item>
                <ion-label position="stacked">From Email</ion-label>
                <ion-input type="email" formControlName="email" placeholder="hello@example.com"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">From Name</ion-label>
                <ion-input formControlName="name" placeholder="Wahl Advertising"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Reply To</ion-label>
                <ion-input type="email" formControlName="replyTo" placeholder="support@example.com"></ion-input>
              </ion-item>
            </ion-list>
            <ion-list lines="full" *ngIf="senderIdentities.length > 0">
              <ion-item *ngFor="let sender of senderIdentities">
                <ion-label>
                  <h3>{{ sender.name || sender.email }}</h3>
                  <p>{{ sender.email }} - {{ sender.verified ? 'domain ownership verified' : 'domain ownership proof required' }}</p>
                  <p>Forgery protection: the API only queues mail from this domain after this exact TXT token is found in DNS.</p>
                  <p>Domain: <code>{{ sender.domain }}</code></p>
                  <p>DNS record type: <code>TXT</code></p>
                  <p>TXT host/name: <code>{{ sender.verification_dns_name }}</code></p>
                  <p>TXT value/token: <code>{{ sender.verification_dns_value }}</code></p>
                  <p *ngIf="sender.verification_checked_at">Last checked: {{ sender.verification_checked_at }}</p>
                  <p *ngIf="sender.verification_error">{{ sender.verification_error }}</p>
                </ion-label>
                <ion-button
                  slot="end"
                  fill="clear"
                  size="small"
                  (click)="copySenderDnsName(sender)">
                  Copy Name
                </ion-button>
                <ion-button
                  slot="end"
                  fill="clear"
                  size="small"
                  (click)="copySenderDnsValue(sender)">
                  Copy Value
                </ion-button>
                <ion-button
                  slot="end"
                  fill="outline"
                  size="small"
                  [disabled]="isVerifyingSenderId === sender.id"
                  (click)="verifySenderIdentity(sender)">
                  <ion-spinner *ngIf="isVerifyingSenderId === sender.id" name="crescent"></ion-spinner>
                  <span *ngIf="isVerifyingSenderId !== sender.id">Verify</span>
                </ion-button>
                <ion-button
                  slot="end"
                  fill="clear"
                  size="small"
                  [disabled]="isRotatingSenderVerificationId === sender.id"
                  (click)="rotateSenderVerification(sender)">
                  <ion-spinner *ngIf="isRotatingSenderVerificationId === sender.id" name="crescent"></ion-spinner>
                  <span *ngIf="isRotatingSenderVerificationId !== sender.id">Rotate</span>
                </ion-button>
              </ion-item>
            </ion-list>
            <ion-note color="medium" *ngIf="senderMessage">{{ senderMessage }}</ion-note>
          </form>

          <form class="marketing-email-panel" [formGroup]="addressForm" (ngSubmit)="saveBusinessAddress()">
            <div class="history-header">
              <h2>Mailing Address</h2>
              <ion-button type="submit" size="small" fill="outline" [disabled]="isSavingAddress">Save</ion-button>
            </div>
            <ion-list lines="full">
              <ion-item>
                <ion-label position="stacked">Label</ion-label>
                <ion-input formControlName="label" placeholder="HQ"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Organization</ion-label>
                <ion-input formControlName="organization" placeholder="Wahl Advertising"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Street</ion-label>
                <ion-input formControlName="streetLine1" placeholder="100 Main St"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Suite</ion-label>
                <ion-input formControlName="streetLine2"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">City</ion-label>
                <ion-input formControlName="city"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">State / Region</ion-label>
                <ion-input formControlName="region"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Postal Code</ion-label>
                <ion-input formControlName="postalCode"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Country</ion-label>
                <ion-input formControlName="country"></ion-input>
              </ion-item>
            </ion-list>
            <ion-note color="medium" *ngIf="addressMessage">{{ addressMessage }}</ion-note>
          </form>
        </ion-col>

        <ion-col size="12" size-xl="8">
          <form class="marketing-email-panel" [formGroup]="campaignForm" (ngSubmit)="saveCampaign()">
            <div class="history-header">
              <h2>Campaign</h2>
              <span class="marketing-email-actions">
                <ion-button type="button" size="small" fill="clear" (click)="newCampaign()">New</ion-button>
                <ion-button type="button" size="small" fill="outline" [disabled]="isSavingTemplate" (click)="saveTemplate()">Save Layout</ion-button>
                <ion-button type="submit" size="small" [disabled]="isSavingCampaign">Save Campaign</ion-button>
              </span>
            </div>

            <ion-list lines="full">
              <ion-item>
                <ion-label position="stacked">Campaign</ion-label>
                <ion-select [ngModel]="selectedCampaignId" [ngModelOptions]="{ standalone: true }" interface="popover" placeholder="Select campaign" (ionChange)="selectCampaignId($event.detail.value)">
                  <ion-select-option *ngFor="let campaign of campaigns" [value]="campaign.id">
                    {{ campaign.name }} - {{ campaign.status }}
                  </ion-select-option>
                </ion-select>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Name</ion-label>
                <ion-input formControlName="name" placeholder="May retail launch"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Subject</ion-label>
                <ion-input formControlName="subject" placeholder="Your spring offer is ready"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Preview Text</ion-label>
                <ion-input formControlName="previewText" placeholder="A short inbox preview"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Template</ion-label>
                <ion-select formControlName="emailTemplateId" interface="popover" placeholder="Optional saved layout" (ionChange)="selectTemplateId($event.detail.value)">
                  <ion-select-option [value]="null">No template</ion-select-option>
                  <ion-select-option *ngFor="let template of templates" [value]="template.id">
                    {{ template.name }}
                  </ion-select-option>
                </ion-select>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Recipient List</ion-label>
                <ion-select formControlName="contactListId" interface="popover" placeholder="All contacts if blank">
                  <ion-select-option [value]="null">All opted-in contacts</ion-select-option>
                  <ion-select-option *ngFor="let list of contactLists" [value]="list.id">
                    {{ list.name }} ({{ list.contact_count }})
                  </ion-select-option>
                </ion-select>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Sender</ion-label>
                <ion-select formControlName="senderIdentityId" interface="popover" placeholder="Required before sending">
                  <ion-select-option *ngFor="let sender of senderIdentities" [value]="sender.id">
                    {{ sender.name || sender.email }} - {{ sender.verified ? 'verified' : 'unverified' }}
                  </ion-select-option>
                </ion-select>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Mailing Address</ion-label>
                <ion-select formControlName="businessMailingAddressId" interface="popover" placeholder="Required before sending">
                  <ion-select-option *ngFor="let address of businessAddresses" [value]="address.id">
                    {{ address.label }} - {{ address.city }}, {{ address.region }}
                  </ion-select-option>
                </ion-select>
              </ion-item>
            </ion-list>

            <ion-list lines="full">
              <ion-item>
                <ion-label>
                  <h3>Send Requirements</h3>
                  <p>Verified sender domain, physical mailing address, unsubscribe host, subject/body, and at least one deliverable recipient are required before sending.</p>
                </ion-label>
              </ion-item>
              <ion-item *ngFor="let requirement of mailingRequirements()">
                <ion-label>
                  <h3>{{ requirement.label }}</h3>
                  <p>{{ requirement.detail }}</p>
                </ion-label>
                <ion-note slot="end" [color]="requirement.complete ? 'success' : 'warning'">
                  {{ requirement.complete ? 'Ready' : 'Needed' }}
                </ion-note>
              </ion-item>
            </ion-list>

            <div class="email-template-builder">
              <div class="email-template-controls">
                <ion-list lines="full">
                  <ion-item>
                    <ion-label position="stacked">Headline</ion-label>
                    <ion-input [(ngModel)]="templateDraft.headline" [ngModelOptions]="{ standalone: true }" (ngModelChange)="onTemplateChanged()"></ion-input>
                  </ion-item>
                  <ion-item>
                    <ion-label position="stacked">Intro</ion-label>
                    <ion-textarea [(ngModel)]="templateDraft.intro" [ngModelOptions]="{ standalone: true }" autoGrow="true" rows="3" (ngModelChange)="onTemplateChanged()"></ion-textarea>
                  </ion-item>
                  <ion-item>
                    <ion-label position="stacked">Highlights</ion-label>
                    <ion-textarea [ngModel]="emailDetailsText" [ngModelOptions]="{ standalone: true }" autoGrow="true" rows="4" placeholder="One highlight per line" (ngModelChange)="setEmailDetailsText($event)"></ion-textarea>
                  </ion-item>
                  <ion-item>
                    <ion-label position="stacked">Button Text</ion-label>
                    <ion-input [(ngModel)]="templateDraft.ctaLabel" [ngModelOptions]="{ standalone: true }" (ngModelChange)="onTemplateChanged()"></ion-input>
                  </ion-item>
                  <ion-item>
                    <ion-label position="stacked">Button URL</ion-label>
                    <ion-input type="url" [(ngModel)]="templateDraft.ctaUrl" [ngModelOptions]="{ standalone: true }" (ngModelChange)="onTemplateChanged()"></ion-input>
                  </ion-item>
                  <ion-item>
                    <ion-label position="stacked">Accent Color</ion-label>
                    <input class="email-style-color" type="color" [ngModel]="templateDraft.style.accentColor" [ngModelOptions]="{ standalone: true }" (ngModelChange)="setTemplateStyleValue('accentColor', $event)" aria-label="Accent color">
                  </ion-item>
                  <ion-item>
                    <ion-label position="stacked">Button Color</ion-label>
                    <input class="email-style-color" type="color" [ngModel]="templateDraft.style.buttonColor" [ngModelOptions]="{ standalone: true }" (ngModelChange)="setTemplateStyleValue('buttonColor', $event)" aria-label="Button color">
                  </ion-item>
                  <ion-item>
                    <ion-label position="stacked">Background Color</ion-label>
                    <input class="email-style-color" type="color" [ngModel]="templateDraft.style.backgroundColor" [ngModelOptions]="{ standalone: true }" (ngModelChange)="setTemplateStyleValue('backgroundColor', $event)" aria-label="Background color">
                  </ion-item>
                  <ion-item>
                    <ion-label position="stacked">Font</ion-label>
                    <ion-select [ngModel]="templateDraft.style.fontFamily" [ngModelOptions]="{ standalone: true }" interface="popover" (ionChange)="setTemplateStyleValue('fontFamily', $event.detail.value)">
                      <ion-select-option value="Arial, sans-serif">Arial</ion-select-option>
                      <ion-select-option value="Helvetica, Arial, sans-serif">Helvetica</ion-select-option>
                      <ion-select-option value="Georgia, serif">Georgia</ion-select-option>
                      <ion-select-option value="Verdana, sans-serif">Verdana</ion-select-option>
                    </ion-select>
                  </ion-item>
                  <ion-item>
                    <ion-label position="stacked">Spacing</ion-label>
                    <ion-select [ngModel]="templateDraft.style.spacing" [ngModelOptions]="{ standalone: true }" interface="popover" (ionChange)="setTemplateStyleValue('spacing', $event.detail.value)">
                      <ion-select-option value="compact">Compact</ion-select-option>
                      <ion-select-option value="comfortable">Comfortable</ion-select-option>
                      <ion-select-option value="spacious">Spacious</ion-select-option>
                    </ion-select>
                  </ion-item>
                </ion-list>
              </div>

              <div class="email-template-preview" [ngStyle]="emailPreviewStyle()" aria-label="Email template preview">
                <div class="email-preview-wrap">
                  <article class="email-preview-card">
                    <section class="email-preview-hero">
                      <p class="email-preview-eyebrow">Wahl Advertising</p>
                      <h1>{{ templateDraft.headline }}</h1>
                      <p>{{ templateDraft.intro }}</p>
                      <a class="email-preview-button" [href]="templateDraft.ctaUrl" target="_blank" rel="noopener noreferrer">{{ templateDraft.ctaLabel }}</a>
                    </section>
                    <section class="email-preview-section">
                      <h2>Highlights</h2>
                      <ul>
                        <li *ngFor="let detail of templateDraft.details">{{ detail }}</li>
                      </ul>
                    </section>
                    <section class="email-preview-qr" *ngIf="templateDraft.hasQrCode">
                      <a *ngIf="templateDraft.qrImageUrl" [href]="templateDraft.ctaUrl" target="_blank" rel="noopener noreferrer">
                        <img [src]="templateDraft.qrImageUrl" width="156" height="156" [alt]="templateDraft.qrLabel">
                      </a>
                      <h2>{{ templateDraft.qrLabel }}</h2>
                      <p>Use the QR code or button to open the linked page.</p>
                      <a class="email-preview-link" [href]="templateDraft.ctaUrl" target="_blank" rel="noopener noreferrer">{{ templateDraft.ctaUrl }}</a>
                    </section>
                  </article>
                </div>
              </div>
            </div>

            <ion-note color="medium" *ngIf="isLoading">Loading marketing data...</ion-note>
            <ion-note color="medium" *ngIf="campaignMessage">{{ campaignMessage }}</ion-note>

            <div class="actions">
              <ion-button type="button" fill="outline" [disabled]="!selectedCampaignId || isSendingCampaign" (click)="sendCampaign()">
                <ion-spinner *ngIf="isSendingCampaign" name="crescent"></ion-spinner>
                <span *ngIf="!isSendingCampaign">Queue Send</span>
              </ion-button>
            </div>
          </form>
        </ion-col>
      </ion-row>
    </ion-grid>
  </div>
</section>
